import { I18n } from "@/i18n/I18n";
import { Trash2 } from "lucide-react";
import { FileIcon } from "./FileIcon";
import { useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { FileUploadWrapper } from "./FileUploadWrapper";

export function FileDragAndDrop(props: { file?: File, setFile: (file: File | undefined) => void, accept?: string, uploading: boolean, uploadProgress: number }) {
  const { file, setFile, accept, uploading, uploadProgress } = props;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | undefined>(undefined);

  // `accept` takes MIME types or extensions, like the input element: a dropped
  // .zip or .xlsx often arrives with a vendor MIME type (or none), so the
  // extension is what identifies it.
  const matchesAccept = (file: File) => {
    if (!accept) return true;
    const acceptedTypes = accept.split(',').map((t) => t.trim()).filter(Boolean);
    return acceptedTypes.some((type) => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      }
      if (type.endsWith('/*')) {
        return file.type.startsWith(type.replace('/*', '/'));
      }
      return file.type === type;
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFile = event.target.files?.[0];
    if (!newFile) return;
    setError(undefined);
    setFile(newFile);
  };
  
  return (
    <FileUploadWrapper uploading={uploading} uploadProgress={uploadProgress}>
      <Form.Group className="mb-3">
        <Form.Label className="form-label-neutral">{I18n.get('uploadNewFile')}</Form.Label>
        <input
          ref={fileInputRef}
          type="file"
          className="d-none"
          onChange={handleFileChange}
          aria-label={I18n.get('selectNewFile')}
          accept={accept}
        />
        {file ? (
          <div className="list-card-item">
            <FileIcon fileName={file.name} />
            <span className="list-card-item-name">{file.name}</span>
            <button
              className="icon-button-small btn-red-light"
              onClick={() => {
                setFile(undefined);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ) : (
          <div
            className="file-drop-zone"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const droppedFile = e.dataTransfer.files[0];
              if (!droppedFile) return;
              if (!matchesAccept(droppedFile)) {
                setError(I18n.get('fileTypeNotAllowed'));
                return;
              }
              setError(undefined);
              setFile(droppedFile);
            }}
          >
            <span className="file-drop-zone-text">
              {I18n.get('dragAndDropOrBrowse')}
            </span>
            <Button className="btn-blue-light fusa-btn-small">
              {I18n.get('selectNewFile')}
            </Button>
          </div>
        )}
        {error && <div className="text-danger mt-1">{error}</div>}
      </Form.Group>
    </FileUploadWrapper>
  );
}
